import { useState } from 'react';
import { Modal } from '../common/Modal';
import { X } from 'lucide-react';
import { Holder, SEM_TITULAR, formatarOab } from '../../utils/holders';

interface MoveToHolderModalProps {
  count: number;
  holders: Holder[];
  onSubmit: (holderId: string | null) => Promise<void>;
  onClose: () => void;
}

// "Mover para titular…" no modo de seleção: muda só a pasta das credenciais, nunca quem tem acesso.
export function MoveToHolderModal({ count, holders, onSubmit, onClose }: MoveToHolderModalProps) {
  const [destino, setDestino] = useState<string>(holders[0]?.id || SEM_TITULAR);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(destino === SEM_TITULAR ? null : destino);
    } catch (err: any) {
      setError(err.message || 'Erro ao mover credenciais');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal pedirParaFechar={onClose}>
      <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: '440px', padding: 'var(--space-8)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>
            Mover {count} credencia{count === 1 ? 'l' : 'is'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Fechar" title="Fechar" className="botao-icone botao-icone--sem-borda">
            <X size={24} />
          </button>
        </div>

        {error && (
          <div style={{ padding: 'var(--space-3)', background: 'var(--color-error-muted)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Para a pasta do titular</label>
          <select value={destino} onChange={(e) => setDestino(e.target.value)} style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }}>
            {holders.map((holder) => (
              <option key={holder.id} value={holder.id}>
                {[holder.name, formatarOab(holder)].filter(Boolean).join(' · ')}
              </option>
            ))}
            <option value={SEM_TITULAR}>Sem titular</option>
          </select>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            Isso só muda a pasta. Quem pode usar cada credencial continua definido em Grupos.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 'var(--space-3)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 'var(--font-medium)' }}>
              Cancelar
            </button>
            <button type="submit" disabled={isSubmitting} style={{ flex: 1, padding: 'var(--space-3)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontWeight: 'var(--font-medium)', opacity: isSubmitting ? 0.6 : 1 }}>
              {isSubmitting ? 'Movendo...' : 'Mover'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
